import React from "react";
import PropTypes from "prop-types";
import { humanEUR } from "../../format";

// pctInt formats a 0..1 breach probability as an integer percent string.
function pctInt(p) {
  return `${Math.round((Number(p) || 0) * 100)}%`;
}

// ResultCard renders a single capped entity (the total or one budget):
// projected baseline→scenario, breach probability, and any expected overage.
function ResultCard({ title, cap, baseline, scenario }) {
  const base = baseline || {};
  const scen = scenario || {};
  const worse =
    (Number(scen.breach_probability) || 0) >
    (Number(base.breach_probability) || 0);
  const accent = worse ? "text-danger" : "text-text-2";

  return (
    <div className="rounded-card border border-card-bd bg-card p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-semibold text-text">{title}</span>
        <span className="text-xs text-text-3">cap {humanEUR(cap)}</span>
      </div>

      <div className="mt-2 text-sm text-text-2">
        <span className="text-text-3">projected: </span>
        <span>{humanEUR(base.projected)}</span>
        <span className={`mx-1 ${accent}`}>→</span>
        <span className={`font-bold ${accent}`}>
          {humanEUR(scen.projected)}
        </span>
      </div>

      <div className="mt-1 text-sm text-text-2">
        <span className="text-text-3">breach: </span>
        <span>{pctInt(base.breach_probability)}</span>
        <span className={`mx-1 ${accent}`}>→</span>
        <span className={worse ? "font-semibold text-danger" : ""}>
          {pctInt(scen.breach_probability)}
        </span>
      </div>

      {Number(scen.expected_overage) > 0 && (
        <div className="mt-1 text-sm text-danger">
          <span className="text-text-3">expected overage: </span>
          <span className="font-semibold">
            {humanEUR(scen.expected_overage)}
          </span>
        </div>
      )}
    </div>
  );
}

ResultCard.propTypes = {
  title: PropTypes.string.isRequired,
  cap: PropTypes.number,
  baseline: PropTypes.object,
  scenario: PropTypes.object,
};

// ScenarioResultCards renders the Total card plus one card per capped budget
// from the score response. Returns null when there is no result yet.
export default function ScenarioResultCards({ result }) {
  if (!result) return null;
  const total = result.total || {};
  const budgets = result.budgets || [];

  return (
    <div className="space-y-3">
      <ResultCard
        title="Total"
        cap={total.cap}
        baseline={total.baseline}
        scenario={total.scenario}
      />
      {budgets.map((b) => (
        <ResultCard
          key={b.id}
          title={b.name}
          cap={b.cap}
          baseline={b.baseline}
          scenario={b.scenario}
        />
      ))}
    </div>
  );
}

ScenarioResultCards.propTypes = {
  /** The score response data: {total:{...}, budgets:[{...}]}. */
  result: PropTypes.shape({
    total: PropTypes.object,
    budgets: PropTypes.arrayOf(PropTypes.object),
  }),
};

ScenarioResultCards.defaultProps = {
  result: null,
};
